export const meta = {
    title: "Project Analysis | CTrees",
    desc: "Trusted carbon and activity data for carbon markets",
    img: "",
};

export const hero = {
    title: "Project Analysis",
    subheader:
        "Trusted scientific data to plan, implement, and monitor nature-based projects.",
    link: "/contact",
    buttonText: "REQUEST A DEMO",
    bgImg:
        "https://ctrees-website.s3.us-west-2.amazonaws.com/webv3/solutions/hero/project-analysis.jpg",
    bgImgStyle: "h-screen bg-black",
    bgImgPosition: {
        mobile: "object-center",
        tablet: "object-center",
        desktop: "object-center",
    },
};

export const difference = {
    heading: "Measure your impact",
    description:
        "Carbon markets are changing. Get ahead with scientific data that meets and exceeds methodology requirements. Our flexible data solutions prepare projects for issuance and verify outcomes.",
    cards: [
        {
            id: 1,
            image:
                "https://ctrees-website.s3.us-west-2.amazonaws.com/webv3/landing/25_01+Data+Card_REDD%2BAI.png",
            imageAlt: "Comprehensive data",
            title: "Comprehensive data",
            description:
                "Access groundbreaking data on carbon and land activity, all backed by peer-reviewed science. Since 2022, CTrees has produced the first global land carbon map, the first global radar alert system, and the first pantropic degradation maps.",
            hoverDescription:
                "",
        },
        {
            id: 2,
            image:
                "https://ctrees-website.s3.us-west-2.amazonaws.com/news/25_09+NYCW_Sassan_speaking.jpeg",
            imageAlt: "Activity & Carbon Data",
            title: "Science expertise",
            description:
                "Engage with experts in remote sensing and forest ecology. CTrees mapped 43 jurisdictions for Verra's VM0048 methodology and built performance monitoring for 8 jurisdictions under ART/TREES. Our scientists regularly publish in leading academic journals.",
            hoverDescription:
                "",
        },
        {
            id: 3,
            image:
                "https://ctrees-website.s3.amazonaws.com/webv3/products/beats/activity-data-beat-4.png",
            title: "Custom analysis",
            description:
                "Generate results based on your needs, for any date range, ecosystem, or stage in the project lifecycle. CTrees works with developers, investors, and standards to deliver scientific insights that matter. CTrees works with developers, investors, and standards to deliver scientific insights that matter.",
            hoverDescription:
                "",
        },
    ],
};

export const participants = {
    heading: "Data for Action",
    ctaText: "Get in touch",
    ctaLink: "/contact",
    items: [
        {
            title: "For Project Developers",
            body: "Evaluate feasibility for new projects, set baselines for registration, and monitor ongoing performance and risk.",
        },
        {
            title: "For Investors",
            body: "Find and evaluate projects to invest in with confidence. Monitor and de-risk project performance.",
        },
    ],
};

export const methodologies = {
    heading: "Scientific Insights",
    description:
        "Work with CTrees scientists to develop actionable maps and metrics for project design and verification. From off-the-shelf data to customized solutions, CTrees makes your project or portfolio’s carbon impact visible.",
    ctaText: "GET IN TOUCH",
    ctaLink: "/contact",
    tabs: [
        {
            id: "redd",
            label: "REDD+",
            image:
                "https://ctrees-website.s3.us-west-2.amazonaws.com/webv3/solutions/project_analysis/projectanalysis_redd.jpg",
            body: {
                title: "Reduced Emissions from Deforestation and Forest Degradation",
                intro: "Calculate avoided emissions and monitor performance in any project or jurisdiction:",
                bullets: [
                    "Measure historical deforestation, degradation, and regrowth to define baselines and additionality.",
                    "Detect forest loss in near-real time.",
                    "Access quarterly land cover change data at 30m resolution with auto-calculated emissions.",
                ],
            },
        },
        {
            id: "arr",
            label: "ARR",
            image:
                "https://ctrees-website.s3.us-west-2.amazonaws.com/webv3/solutions/project_analysis/projectanaylsis_ARR.jpg",
            body: {
                title: "Afforestation, Reforestation, and Revegetation",
                intro: "Identify restoration areas, establish dynamic baselines, and track growth over time:",
                bullets: [
                    "Evaluate high-potential degraded or low-biomass lands with 30m carbon data.",
                    "Predict carbon accumulation using forest growth curves.",
                    "Monitor canopy growth, regrowth rates, and disturbances.",
                ],
            },
        },
        {
            id: "ifm",
            label: "IFM",
            image:
                "https://ctrees-website.s3.us-west-2.amazonaws.com/webv3/solutions/project_analysis/projectanalysis_IFM.jpg",
            body: {
                title: "Improved Forest Management",
                intro: "Quantify improved carbon storage and avoided emissions in managed forests:",
                bullets: [
                    "Assess historic harvest rates and carbon stocks to identify additionality opportunities.",
                    "Monitor biomass changes to detect harvest activities or regeneration.",
                    "Aggregate and compare performance across projects and jurisdictions.",
                ], 
            },
        },
        {
            id: "blue",
            label: "Blue carbon",
            image:
                "https://ctrees-website.s3.us-west-2.amazonaws.com/webv3/solutions/project_analysis/projectanalysis_bluecarbon.jpg",
            body: {
                title: "Taking stock in Myanmar",
                text: "For a large buyer considering portfolio investment in blue carbon, CTrees provided independent evaluation of three mangrove restoration projects in Myanmar and India. With data tailored to local conditions, CTrees scientists evaluated biomass carbon and activity in the project site, surrounding areas, and jurisdiction. CTrees compared its results to data reported under VM0048. The information informed the buyers’ credit purchasing decisions.",
            },
        },
    ],
};

export const lifeCycle = {
    title: "Project Life Cycle",
    subheader:
        "High-quality analytics to take your carbon investment through the life cycle.",
    items: [
        {
            id: "feasibility",
            title: "1. Feasibility & due diligence",
            body: "Evaluate project potential with desktop assessments, historical baselines, and risk screening to determine viability before investment.",
            image:
                "https://ctrees-website.s3.us-west-2.amazonaws.com/webv3/solutions/beats/feasibility.png",
            imageAlt: "Feasibility and due diligence analytics",
        },
        {
            id: "baselines",
            title: "2. Baseline setting",
            body: "Establish accurate baselines with scenario modeling, leakage analysis, and MRV planning to ensure compliance and optimize project design.",
            image:
                 "https://ctrees-website.s3.us-west-2.amazonaws.com/webv3/solutions/beats/baseline.png",
            imageAlt: "Baselines setting analytics",
        },
        {
            id: "monitoring",
            title: "3. Monitoring & verification",
            body: "Track project performance with real-time alerts, disturbance monitoring, and annual reporting to support verification and credit issuance.",
            image:
                "https://ctrees-website.s3.us-west-2.amazonaws.com/webv3/solutions/beats/monitoring.png",
            imageAlt: "Monitoring and verification dashboard",
        },
    ],
};

export const dataDeliverables = {
    heading: "Available Data",
    tableData: [
        {
            id: 1,
            dataset: "Forest cover change",
            resolution: "30m",
            geographicExtent: "Wet tropics",
            temporalCoverage: "1990-2025, annual",
            metrics: "Deforestation, degradation, primary & secondary forests",
        },
        {
            id: 2,
            dataset: "Forest productivity",
            resolution: "100m",
            geographicExtent: "Global",
            temporalCoverage: "Average over past 40 years",
            metrics: "Carbon growth curves",
        },
        {
            id: 3,
            dataset: "Forest change alerts",
            resolution: "10m",
            geographicExtent: "Global",
            temporalCoverage: "2018-present, every 2 weeks",
            metrics: "Reversal risk",
        },
        {
            id: 4,
            dataset: "Biomass carbon",
            resolution: "100m and 30m",
            geographicExtent: "Global",
            temporalCoverage: "2000-2025, annual",
            metrics: "Carbon stocks, emissions, removals",
        },
        {
            id: 5,
            dataset: "Soil carbon",
            resolution: "100m",
            geographicExtent: "Global",
            temporalCoverage: "2020",
            metrics: "Carbon stock",
        },
    ],
};